import * as Dialog from '@radix-ui/react-dialog';
import clsx from 'clsx';
import { useEffect, useMemo, useState, type JSX } from 'react';
import { isPropertyMediaVideoUrl } from '@/lib/storage/propertyMedia';

interface PublicDetailHeroProps {
  cover: { url: string; alt: string } | null;
  badges: string[];
  title: string;
  meta: string[];
  galleryCount?: number;
  gallery?: Array<{ url: string; alt: string }>;
  heightClassName?: string;
}

export function PublicDetailHero({
  cover,
  badges,
  title,
  meta,
  galleryCount,
  gallery,
  heightClassName = 'h-[320px] md:h-[460px]',
}: PublicDetailHeroProps): JSX.Element {
  const hasGallery = typeof galleryCount === 'number' && galleryCount > 1;
  const galleryItems = useMemo(() => gallery ?? [], [gallery]);
  const hasModal = galleryItems.length > 0;
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open || galleryItems.length === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setActiveIndex((index) => Math.min(index + 1, galleryItems.length - 1));
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setActiveIndex((index) => Math.max(index - 1, 0));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, galleryItems.length]);

  useEffect(() => {
    if (activeIndex >= galleryItems.length) {
      setActiveIndex(0);
    }
  }, [activeIndex, galleryItems.length]);

  const heroContent = (
    <div className={clsx('relative w-full', heightClassName)}>
      {cover ? (
        isPropertyMediaVideoUrl(cover.url) ? (
          <video
            src={cover.url}
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            aria-label={cover.alt}
          />
        ) : (
          <img
            src={cover.url}
            alt={cover.alt}
            className="absolute inset-0 h-full w-full object-cover"
            decoding="async"
            loading="eager"
          />
        )
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/15 via-brand-accent/10 to-surface-subtle" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/5" />
      {hasGallery ? (
        <div className="pointer-events-none absolute right-4 top-4 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white md:right-6 md:top-6">
          <span>
            1<span className="mx-1 opacity-70">/</span>
            {galleryCount}
          </span>
          <span className="hidden text-[11px] font-normal uppercase tracking-wide text-white/80 sm:inline">
            More media
          </span>
        </div>
      ) : null}
      <div className="relative flex h-full flex-col justify-end gap-4 p-6 md:p-10">
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80">
          {badges.map((badge) => (
            <span key={badge} className="rounded-full bg-white/15 px-3 py-1">
              {badge}
            </span>
          ))}
        </div>
        <div className="flex flex-col gap-2 text-white">
          <h1 className="text-3xl font-semibold leading-tight md:text-4xl">{title}</h1>
          <div
            className="flex flex-col gap-1 text-sm text-white/90 md:flex-row md:items-center md:gap-3 md:text-base"
            data-ui="Public.PropertyDetail.HeroMeta"
          >
            {meta.map((item, index) => (
              <span key={item}>
                {item}
                {index === 0 && meta.length > 1 ? (
                  <span className="hidden h-4 w-px bg-white/40 md:inline-block" aria-hidden />
                ) : null}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section className="-mx-4 overflow-hidden bg-white sm:-mx-6 md:-mx-8 lg:-mx-10">
      {hasModal ? (
        <Dialog.Root
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);
            if (nextOpen) {
              setActiveIndex(0);
            }
          }}
        >
          <Dialog.Trigger asChild>
            <button
              type="button"
              className="block w-full cursor-zoom-in text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary"
              aria-label="Open media gallery"
            >
              {heroContent}
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" />
            <Dialog.Content className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 px-4 py-6 md:px-10">
              <Dialog.Title className="sr-only">Media gallery</Dialog.Title>
              <Dialog.Description className="sr-only">
                Use arrow keys to move between media items.
              </Dialog.Description>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="absolute right-4 top-4 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-sm transition hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
                >
                  Close
                </button>
              </Dialog.Close>
              <div className="flex w-full max-w-6xl flex-col items-center gap-4">
                <div className="relative w-full">
                  {isPropertyMediaVideoUrl(galleryItems[activeIndex]?.url ?? '') ? (
                    <video
                      src={galleryItems[activeIndex]?.url ?? ''}
                      className="max-h-[70vh] w-full rounded-2xl object-contain"
                      controls
                      preload="metadata"
                      playsInline
                      aria-label={galleryItems[activeIndex]?.alt ?? ''}
                    />
                  ) : (
                    <img
                      src={galleryItems[activeIndex]?.url ?? ''}
                      alt={galleryItems[activeIndex]?.alt ?? ''}
                      className="max-h-[70vh] w-full rounded-2xl object-contain"
                    />
                  )}
                  <div className="absolute inset-y-0 left-0 flex items-center">
                    <button
                      type="button"
                      className={clsx(
                        'ml-3 rounded-full bg-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-sm transition',
                        activeIndex === 0
                          ? 'cursor-not-allowed opacity-40'
                          : 'hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80',
                      )}
                      onClick={() => {
                        setActiveIndex((index) => Math.max(index - 1, 0));
                      }}
                      disabled={activeIndex === 0}
                      aria-label="Previous photo"
                    >
                      Prev
                    </button>
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center">
                    <button
                      type="button"
                      className={clsx(
                        'mr-3 rounded-full bg-white/10 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white backdrop-blur-sm transition',
                        activeIndex >= galleryItems.length - 1
                          ? 'cursor-not-allowed opacity-40'
                          : 'hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80',
                      )}
                      onClick={() => {
                        setActiveIndex((index) => Math.min(index + 1, galleryItems.length - 1));
                      }}
                      disabled={activeIndex >= galleryItems.length - 1}
                      aria-label="Next photo"
                    >
                      Next
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-white/80">
                  <span>
                    {activeIndex + 1} / {galleryItems.length}
                  </span>
                  <span className="hidden text-[11px] font-normal uppercase tracking-wide text-white/60 sm:inline">
                    Use arrow keys to navigate
                  </span>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      ) : (
        heroContent
      )}
    </section>
  );
}
